import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import type { AdminPlan } from '@/api/types';
import { useEffect, useMemo, useState } from 'react';
import { getWebApp, haptic } from '@/lib/telegram';
import { normalizePeriodDays, periodLabel } from '@/lib/period';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';

export const Route = createFileRoute('/plans')({ component: AdminPlans });

function AdminPlans() {
  const { t } = useTranslation();
  const { data: initialPlans, isPending } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => api.plans(),
    placeholderData: keepPreviousData,
  });
  // Живой CBR-курс USD→RUB. base/floor приходят в USD-центах — конвертим в рубли
  // по живому курсу (раньше показывали 1:1 как при курсе 100 → завышенные цены).
  const { data: fxRates } = useQuery({ queryKey: ['fxRates'], queryFn: () => api.fxRates() });
  const usdToRub = useMemo(
    () => fxRates?.find((r) => r.code === 'RUB')?.perRub ?? 100,
    [fxRates],
  );
  const [plans, setPlans] = useState<AdminPlan[]>([]);

  useEffect(() => {
    if (initialPlans) setPlans(initialPlans);
  }, [initialPlans]);

  const { mutateAsync: savePlan } = useMutation({
    mutationFn: (p: AdminPlan) =>
      api.updatePlan(p.id, {
        visible: p.visible,
        is_special: p.isSpecial,
        // Рубли → копейки. 0 читается бэком как «снять фиксацию».
        fixed_retail_display_minor: p.fixedRetailRub ? Math.round(p.fixedRetailRub * 100) : 0,
      }),
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    tg.BackButton.hide();
    tg.MainButton.setText(t('Save').toUpperCase());
    tg.MainButton.show();
    const onMain = async () => {
      // allSettled: показываем сколько упало, но не глотаем тихо.
      const results = await Promise.allSettled(plans.map((p) => savePlan(p)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed === 0) {
        haptic('success');
        toast.success(t('Saved'));
      } else {
        haptic('error');
        toast.error(`Не удалось сохранить ${failed} из ${plans.length} планов`);
      }
    };
    tg.MainButton.onClick(onMain);
    return () => {
      tg.MainButton.offClick(onMain);
      tg.MainButton.hide();
    };
  }, [t, plans, savePlan]);

  const updatePlan = (id: string, patch: Partial<typeof plans[0]>) => {
    setPlans((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  // Сортируем по каноническому периоду, потом devices, потом name —
  // линейный список становится предсказуемым (а не сюрпризом от AdaptGroup).
  const sortedPlans = useMemo(
    () =>
      [...plans].sort(
        (a, b) =>
          normalizePeriodDays(a.days) - normalizePeriodDays(b.days) ||
          a.devices - b.devices ||
          a.name.localeCompare(b.name),
      ),
    [plans],
  );

  // Подозрения на дубли по (canonical_period, devices) среди visible-планов.
  // Активный план обычно один на такую пару — пара = баг данных у AdaptGroup.
  const duplicateWarnings = useMemo(() => {
    const buckets = new Map<string, AdminPlan[]>();
    for (const p of plans.filter((x) => x.visible)) {
      const key = `${normalizePeriodDays(p.days)}-${p.devices}`;
      const arr = buckets.get(key) ?? [];
      arr.push(p);
      buckets.set(key, arr);
    }
    return Array.from(buckets.values()).filter((arr) => arr.length > 1);
  }, [plans]);

  return (
    <motion.div initial={false} className="p-4 space-y-4 pb-24">
      <h2 className="text-2xl font-bold tracking-tight px-2 pt-2">{t('Plans')}</h2>
      <p className="text-sm text-muted-foreground px-2">
        Настройки тарифов AdaptGroup в нашем кэше. Изменения видят все партнёры.
      </p>

      {duplicateWarnings.length > 0 && (
        <Card className="border-yellow-500/30 bg-yellow-500/5">
          <CardContent className="p-4 space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-yellow-500 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden />
              Дубли в каталоге AdaptGroup
            </div>
            <p className="text-[11px] text-muted-foreground">
              Несколько видимых планов на одну пару «период × устройства». Почисти у AdaptGroup,
              иначе клиент в боте увидит «{
              duplicateWarnings[0]?.[0] && periodLabel(duplicateWarnings[0][0].days)
              }» дважды.
            </p>
            <ul className="text-[11px] space-y-1">
              {duplicateWarnings.map((arr, i) => (
                <li key={i} className="text-yellow-500/90">
                  • {periodLabel(arr[0].days)} × {arr[0].devices} устр.:{' '}
                  {arr.map((p) => `«${p.name}»`).join(' · ')}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {isPending && plans.length === 0 ? (
          <>
            <Skeleton className="h-[180px] rounded-2xl" />
            <Skeleton className="h-[180px] rounded-2xl" />
            <Skeleton className="h-[180px] rounded-2xl" />
            <Skeleton className="h-[180px] rounded-2xl" />
          </>
        ) : null}
        {sortedPlans.map((plan) => (
          <Card key={plan.id}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold truncate">{plan.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {periodLabel(plan.days)} · {plan.devices} устр. ·{' '}
                    {plan.trafficGb === null ? '∞' : `${plan.trafficGb} GB`}
                  </div>
                </div>
                <Switch checked={plan.visible} onCheckedChange={(v) => updatePlan(plan.id, { visible: v })} />
              </div>

              {/* Цены приходят синком AdaptGroup + платформенной наценкой — read-only.
                  Наценку задаёшь в Ops → Платформа → Наценки. */}
              <div className="grid grid-cols-2 gap-3">
                <CostBadge label="Себестоимость" valueCents={plan.basePriceRub} rate={usdToRub} muted />
                <CostBadge label="С наценкой" valueCents={plan.floorRub} rate={usdToRub} />
              </div>

              {/* Спецтариф: вне сетки периодов, жёсткая рублёвая цена, наценку
                  партнёра к нему не применяем — вся маржа остаётся платформе. */}
              <div className="pt-3 border-t border-white/[0.06] space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-bold">Спецтариф</div>
                    <div className="text-[11px] text-muted-foreground leading-snug">
                      Отдельной карточкой у клиента, вне выбора срока
                    </div>
                  </div>
                  <Switch
                    checked={plan.isSpecial}
                    onCheckedChange={(v) => updatePlan(plan.id, { isSpecial: v })}
                  />
                </div>
                {plan.isSpecial && (
                  <div className="space-y-1.5">
                    <label className="text-[11px] text-muted-foreground uppercase tracking-wider font-bold">
                      Жёсткая цена, ₽
                    </label>
                    <input
                      type="number"
                      min={0}
                      inputMode="numeric"
                      value={plan.fixedRetailRub ?? ''}
                      placeholder="без фиксации"
                      onChange={(e) =>
                        updatePlan(plan.id, {
                          fixedRetailRub: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      className="w-full rounded-xl px-3 py-2 text-sm font-mono bg-white/5 text-foreground"
                    />
                    <div className="text-[11px] text-muted-foreground leading-snug">
                      Партнёр наценку не крутит, вся маржа платформе. Пусто = обычный расчёт.
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </motion.div>
  );
}

function CostBadge({ label, valueCents, rate, muted }: { label: string; valueCents: number; rate: number; muted?: boolean }) {
  // value в USD-центах → рубли по живому CBR-курсу (rate = RUB per USD).
  // cents × rate / 100. Округляем до рубля — копейки тут шум.
  const rub = Math.round((valueCents * rate) / 100);
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] text-muted-foreground uppercase tracking-wider font-bold">{label}</label>
      <div
        className={`w-full rounded-xl px-3 py-2 text-sm font-mono ${
          muted ? 'bg-white/[0.02] text-muted-foreground' : 'bg-white/5 text-foreground'
        }`}
      >
        {rub.toLocaleString('ru')} ₽
      </div>
    </div>
  );
}
